<template>
  <div>
    <BannerHero
      segment="enterprise"
      :badge="{
        text: 'Cloud',
        icon: IconCloud,
      }"
      :heading="`Supp<span class='uppercase'>o</span>rt and protect your organisation with ent<span class='uppercase'>e</span>rprise level cl<span class='uppercase'>o</span>ud technology`"
      description="Want to move from on-premises to the cloud? Or do you need to solidify your data protection infrastructure? Aussie Broadband's expert team can help."
      :button-primary="{
        label: 'Speak to an Expert',
        url: '#contact',
        arrow: 'right',
      }"
      :button-secondary="{
        label: 'Download our ebook',
        url: '#ebook',
        arrow: 'right',
      }"
      :usps="uspData"
      class="!mb-8"
    />

    <AlertBanner
      type="success"
      :content="{
        heading: 'Need a VMWare partner? Aussie Broadband can help.',
        body: 'The Aussie Broadband team is thrilled to be a Premier Partner in the Broadcom Advantage Partner Program for VMware Cloud Service Providers (VCSP).',
        link: {
          url: '/enterprise/lp/vmware/',
          label: 'Find out more',
        },
      }"
      class="spacing-color border-t"
    />
    <section
      class="container spacing-white flex flex-col items-start gap-12 md:flex-row lg:gap-16"
    >
      <div>
        <div class="tag">Cloud Services</div>
        <h2>Cloud technology hosted in Australia data centres.</h2>
        <p>
          Overcoming tech debt is tough without help. Even in large businesses,
          it's rare to have all the resources required to keep your environment
          current, secure and operating at a high level. Making the switch to
          cloud technology can help ease the tech debt burden.
        </p>
        <MediaYoutube
          video-id="HgzHu7vAhoA"
          title="Overcoming Tech Debt"
          class="mb-12"
        />
      </div>
      <MediaImgSkew
        class="order-first md:order-last"
        img-src="/img/enterprise/cloud-1-768x768.webp"
        img-alt="Two people looking at a laptop in a server room"
        label="expert ad<span class='uppercase'>v</span>ice and im<span class='uppercase'>p</span>lementation"
      />
    </section>
    <section class="container spacing-color">
      <div class="tag">Tailored Solutions</div>
      <h2>Our Cloud Solutions.</h2>
      <p>
        Keep your data onshore, close by and accessible with Aussie Broadband
        cloud technology. Leverage our expert team for advice, engineering and
        implementation of your cloud migration and ongoing operation of your
        organisation's data and virtual workloads.
      </p>
      <div
        class="mb-8 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-16 lg:grid-cols-3"
      >
        <div v-for="(item, idx) in cloudSolutionsData" :key="idx">
          <component :is="item.icon" class="mb-8 h-12 w-12 fill-green-800" />
          <h3>{{ item.title }}</h3>
          <template v-for="(contentItem, index) in item.content" :key="index">
            <p>{{ contentItem }}</p>
          </template>
          <h3 class="!text-lg md:!text-xl">{{ item.subTitle }}</h3>
          <template v-for="(contentItem, index) in item.subItems" :key="index">
            <p class="!mb-6 flex items-start gap-3 md:mb-8">
              <IconAlertSuccess class="h-6 w-6 shrink-0 fill-green-800" />
              <span>{{ contentItem }}</span>
            </p>
          </template>
        </div>
      </div>
    </section>
    <section id="ebook">
      <BannerHeroBlank segment="enterprise" :dark="true">
        <div class="container flex gap-16">
          <div class="text-white">
            <div class="tag !text-green-500">eBook Downloads</div>
            <h2 class="text-white">Start your cloud journey here.</h2>
            <p class="!mb-8">
              Set a platform for a smooth cloud transformation with some expert
              insights.
            </p>
            <div
              class="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-16 lg:grid-cols-3"
            >
              <div
                v-for="(item, idx) in ebookDownloadData"
                :key="idx"
                class="flex flex-col"
              >
                <component
                  :is="item.icon"
                  class="mb-8 h-12 w-12 fill-green-500"
                />
                <h3 class="text-white">{{ item.title }}</h3>
                <p class="!mb-8 grow">{{ item.content }}</p>
                <BtnPrimary
                  :label="item.linkName"
                  size="large"
                  arrow="right"
                  :on-dark="true"
                  @click="openModel(item.linkName, item.link)"
                />
              </div>
            </div>
          </div>
        </div>
      </BannerHeroBlank>
    </section>
    <section
      class="container mb-8 flex flex-col items-center gap-16 md:flex-row"
    >
      <div>
        <div class="tag">Colocation</div>
        <h2>Plug into our world class data centres.</h2>
        <p>
          Colocation gives you the benefits of a data centre without the costs
          of having to invest in one of your own. Our colocation infrastructure
          delivers secure physical housing for your equipment with the hardware,
          links and uninterrupted power supply that ensure business continuity.
        </p>
        <p class="!mb-8">
          Aussie Broadband has a range of data centre options throughout
          Australia that help reduce your latency and keep you in close
          proximity to your data.
        </p>
      </div>
      <MediaImgSkew
        class="order-first md:order-last"
        img-src="/img/enterprise/cloud-2-768x768.webp"
        img-alt="Data Center"
        label="data centre opti<span class='uppercase'>o</span>ns througHout austr<span class='uppercase'>a</span>lia"
        text-size="lg:!text-3xl"
      />
    </section>
    <section class="container spacing-white">
      <h3 class="!mb-8">Aussie Broadband colocation gives you:</h3>
      <div
        class="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-6 lg:gap-16"
      >
        <div v-for="(item, idx) in colocationData" :key="idx">
          <component
            :is="item.icon"
            class="mb-6 h-12 w-12 fill-green-800 lg:mb-8"
          />
          <p class="text-lg font-semibold">
            {{ item.title }}
          </p>
        </div>
      </div>
    </section>
    <section id="contact" class="container spacing-white">
      <FormEnterpriseEnquiry
        heading="Contact us"
        title="Talk to our team about cloud opportunities for your organisation."
        desc="Enter your details below and a member of our team will reach out for an initial chat."
      />
    </section>
    <Modal
      class="!max-w-[480px]"
      :modal-active="modalActive"
      @close-modal="modalActive = !modalActive"
    >
      <FormEbookModalCustom
        :title="modalFormTitle"
        :link="modalFormLink"
        :modal-active="modalActive"
        @close-modal="modalActive = false"
      />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import IconCloud from "@/components/icon/Cloud.vue";

const modalActive = ref(false);
const modalFormTitle = ref("");
const modalFormLink = ref("");
const openModel = (title: string, link: string) => {
  modalFormTitle.value = title;
  modalFormLink.value = link;
  modalActive.value = !modalActive.value;
};

const uspData = [
  {
    name: "Customised infrastructure",
    icon: resolveComponent("IconBuilding"),
  },
  {
    name: "Flexible commercial models",
    icon: resolveComponent("IconControl"),
  },
  {
    name: "Secure safeguards and recovery",
    icon: resolveComponent("IconSecure"),
  },
];

const cloudSolutionsData = [
  {
    title: "Private Cloud",
    content: [
      "Aussie Broadband's Private Cloud gives your organisation isolated cloud consumption infrastructure coupled with a shared management function.",
      "Private Cloud delivers an industry leading cloud environment and data centre provider for physical, virtual or shared options.",
      "We'll assess the value of your data and recommend architecture that transforms your IT environment into a secure, high-performance system.",
    ],
    subTitle: "Private Cloud gives you access to: ",
    subItems: [
      "Agile and scalable infrastructure",
      "Load balancing and remote back ups",
      "High performance through pooled resources",
      "Local data storage for improved latency",
    ],
    icon: resolveComponent("IconPrivateCloud"),
  },
  {
    title: "Data Protection",
    content: [
      "Protect the growth of your organisation with integrated hardware and software that supports you in the event of disaster scenario.",
      "Aussie Broadband's data protection solutions deliver business continuity when you really need it through duplication and reinstatement of your critical systems. Maintain peace of mind, in the event of malicious activity, unintentional deletion or natural disaster.",
      "Our robust data protection platform strengthens your security posture and ensures you can get back to business fast if the worst occurs.",
    ],
    subTitle: "Our Data Protection solutions deliver:",
    subItems: [
      "Data protection as a service",
      "Easy scaling up and down",
      "Load balancing and remote back ups",
      "Rapid recovery in disaster scenarios",
      "Customised compliance requirements",
    ],
    icon: resolveComponent("IconWebShield"),
  },
  {
    title: "Cloud Storage",
    content: [
      "Customise your storage environment with high-performance Object and File solutions that efficiently manage your data and workloads.",
      "Aussie Broadband's storage gives you the ability to customise your solution across shared or dedicated environments with consumption or quota-based setups.",
      "Large organisations invariably have to manage large quantities of data. Equip your organisation with the capability and workload support of a high-performance storage environment.",
    ],
    subTitle: "Our team can implement:",
    subItems: [
      "Multiple storage tiers and classes",
      "High availability and performance",
      "Secure tenancy and data encryption at rest",
      "Tailored scalability and elasticity",
    ],
    icon: resolveComponent("IconCloudNet"),
  },
];

const ebookDownloadData = [
  {
    title: "Take your company to the cloud",
    content:
      "Need help with your cloud migration? Learn about the key steps your business needs to take by downloading our eBook.",
    linkName: "Cloud migration ebook",
    link: "https://assets.aussiebroadband.com.au/web/assets/marketing/ebooks/abb-taking-your-company-to-the-cloud.pdf",
    icon: resolveComponent("IconCloud"),
  },
  {
    title: "Take steps towards data security",
    content:
      "Discover how to design your organisation's data protection framework with these 6 steps.",
    linkName: "Data Security ebook",

    link: "https://assets.aussiebroadband.com.au/web/assets/marketing/ebooks/abb-take-steps-towards-data-security.pdf",
    icon: resolveComponent("IconSecure"),
  },
  {
    title: "Making sense of storage",
    content:
      "Build the right storage solution for your IT environment with some expert insights.",
    linkName: "Storage solution ebook",

    link: "https://assets.aussiebroadband.com.au/web/assets/marketing/ebooks/abb-making-sense-of-storage.pdf",
    icon: resolveComponent("IconServerRack"),
  },
];

const colocationData = [
  {
    title: "World-class security on site",
    icon: resolveComponent("IconSecure"),
  },
  {
    title: "24-hour site access",
    icon: resolveComponent("IconRemoteSupport"),
  },
  { title: "High-speed connectivity", icon: resolveComponent("IconZap") },
  { title: "Dual power supply", icon: resolveComponent("IconDualPower") },
  { title: "Remote hands services", icon: resolveComponent("IconClock12") },
  { title: "Data cabling supplies", icon: resolveComponent("IconFibre") },
];

const metaStore = useMetaStore();
metaStore.pageTitle = "Enterprise Managed Cloud | Aussie Broadband";
metaStore.metaTitle = "Managed Cloud Services at the Enterprise Level";
metaStore.metaDescription =
  "Keep your data onshore, close by and accessible with Aussie Broadband managed cloud services. Leverage our expert team for advice, engineering and implementation of your cloud migration.";
</script>
